import type { AiGeneratedImage } from 'mock/generated/types';

import { useActiveOriginalImage } from 'src/modules/homeownerApp/hooks';
import { MY_UPLOAD_STYLE } from 'src/modules/homeownerApp/static_values';
import type { AiOrSampleOriginalImage } from 'src/modules/homeownerApp/types';
import { isSample } from 'src/modules/homeownerApp/types';
import type { AiDisplayImage } from 'src/shared/graphql/generated';
import {
  useAiAvailableConfigurationsQuery,
  usePublicOrgInfoQuery,
} from 'src/shared/graphql/generated';
import { useGlobalStorageContext } from 'src/shared/hooks/useGlobalStorage';

type FeaturedDisplayImage = AiDisplayImage & {
  style: string;
  aiGeneratedImage: { thumbnailUrl: string };
};

interface UseFeaturedDisplayImagesResponse {
  image?: AiOrSampleOriginalImage | null;
  featuredDisplayImages?: FeaturedDisplayImage[];
  loading: boolean;
  supportedStylesAndOriginal: AiGeneratedImage['style'][];
}

/**
 * Returns the featured display images, filtered by the supported styles.
 * This will respect the org's enabled styles for the given space type, as long
 * as the affOrgPublicId is set in the global storage context.
 */
export const useFeaturedDisplayImages = (
  aiOriginalImageId: number,
): UseFeaturedDisplayImagesResponse => {
  const { image, loading } = useActiveOriginalImage({
    originalImageId: aiOriginalImageId,
    variables: { featured: true },
  });

  const { affOrgPublicId } = useGlobalStorageContext();
  const { data: publicOrgInfo, loading: publicOrgInfoLoading } =
    usePublicOrgInfoQuery({
      variables: { publicIdentifier: affOrgPublicId || '' },
      skip: !affOrgPublicId,
    });
  const orgId = publicOrgInfo?.publicOrgInfo?.id;

  const { data: aiAvailableStylesData } = useAiAvailableConfigurationsQuery({
    variables: {
      orgId: orgId ? Number(orgId) : undefined,
      spaceType: image?.spaceType || 'exterior',
    },
    ssr: false,
  });
  const supportedStyles =
    aiAvailableStylesData?.aiAvailableConfigurations?.styles;

  // We want to include My upload, and have it as the first item in the list.
  // The other ordering doesn't matter, so we can just use whatever ordering the API gives us.
  const supportedStylesAndOriginal = [
    MY_UPLOAD_STYLE,
    ...(supportedStyles || []),
  ];

  if (isSample(image)) {
    return {
      image,
      featuredDisplayImages: [],
      loading: false,
      supportedStylesAndOriginal,
    };
  }

  const images: FeaturedDisplayImage[] =
    image?.aiDisplayImages?.filter?.(
      (aiDisplayImage): aiDisplayImage is FeaturedDisplayImage =>
        !!aiDisplayImage.style &&
        !!aiDisplayImage.aiGeneratedImage?.thumbnailUrl &&
        supportedStylesAndOriginal?.includes(aiDisplayImage.style),
    ) || [];

  const featuredDisplayImages = images.sort(
    (a: FeaturedDisplayImage, b: FeaturedDisplayImage) => {
      const aIndex = supportedStylesAndOriginal?.indexOf(a.style);
      const bIndex = supportedStylesAndOriginal?.indexOf(b.style);
      return aIndex - bIndex;
    },
  ) satisfies FeaturedDisplayImage[];

  return {
    image,
    featuredDisplayImages,
    loading: loading || publicOrgInfoLoading || !image,
    supportedStylesAndOriginal,
  };
};
